import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Star, Calendar, Building2, Loader2, Trash2, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from '@/hooks/use-toast';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Company } from '@/types/company';
import { Playlist } from '@/types/playlist';
import { heroPlaylistService } from '@/services/heroPlaylistService';

interface SendToHeroDialogProps {
  isOpen: boolean;
  onClose: () => void;
  playlist: Playlist | null;
  onSuccess: () => void;
}

export function SendToHeroDialog({
  isOpen,
  onClose,
  playlist,
  onSuccess
}: SendToHeroDialogProps) {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [useSchedule, setUseSchedule] = useState(false);
  const [useTargeting, setUseTargeting] = useState(false);
  const [targetCompanyIds, setTargetCompanyIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingHeroPlaylistId, setExistingHeroPlaylistId] = useState<string | null>(null);
  const { toast } = useToast();

  // Firmaları getir - dialog açıkken her zaman yükle
  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/companies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Firma verileri alınamadı');
      return response.json();
    },
    enabled: isOpen
  });

  const resetForm = () => {
    setExistingHeroPlaylistId(null);
    setIsActive(true);
    setStartDate('');
    setEndDate('');
    setUseSchedule(false);
    setUseTargeting(false);
    setTargetCompanyIds([]);
    setSearchTerm('');
  };

  useEffect(() => {
    if (!isOpen || !playlist) return;

    const loadExisting = async () => {
      setIsLoading(true);
      try {
        const result = await heroPlaylistService.checkPlaylistInHero(playlist._id);

        if (result.isInHero && result.heroPlaylist) {
          const hp = result.heroPlaylist;
          setExistingHeroPlaylistId(hp._id || null);
          setIsActive(hp.active !== false);
          setUseSchedule(!!(hp.startDate || hp.endDate));
          setStartDate(hp.startDate ? new Date(hp.startDate).toISOString().slice(0, 16) : '');
          setEndDate(hp.endDate ? new Date(hp.endDate).toISOString().slice(0, 16) : '');

          const companyIds = hp.targetCompanies?.map((c: any) => typeof c === 'string' ? c : c._id) || [];
          setUseTargeting(companyIds.length > 0);
          setTargetCompanyIds(companyIds);
          setSearchTerm('');
        } else {
          resetForm();
        }
      } catch {
        resetForm();
      } finally {
        setIsLoading(false);
      }
    };

    loadExisting();
  }, [isOpen, playlist]);

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Banner gibi company toggle
  const handleCompanyToggle = (companyId: string) => {
    setTargetCompanyIds(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const selectAllCompanies = () => {
    const ids = (searchTerm ? filteredCompanies : companies).map(c => c._id);
    setTargetCompanyIds(ids);
  };

  const clearAllCompanies = () => {
    setTargetCompanyIds([]);
  };

  const handleSubmit = async () => {
    if (!playlist) return;

    try {
      setIsSubmitting(true);

      const data = {
        playlist: playlist._id,
        startDate: useSchedule && startDate ? startDate : null,
        endDate: useSchedule && endDate ? endDate : null,
        targetCompanies: useTargeting ? targetCompanyIds : [],
        active: isActive
      };

      if (existingHeroPlaylistId) {
        await heroPlaylistService.update(existingHeroPlaylistId, data);
        toast({
          title: "Başarılı",
          description: `"${playlist.name}" hero ayarları güncellendi.`,
        });
      } else {
        await heroPlaylistService.create(data);
        toast({
          title: "Başarılı",
          description: `"${playlist.name}" hero'ya eklendi.`,
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Hero kaydetme hatası:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || "Hero'ya eklenirken bir hata oluştu.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingHeroPlaylistId) return;

    try {
      setIsDeleting(true);
      await heroPlaylistService.delete(existingHeroPlaylistId);
      toast({
        title: "Başarılı",
        description: `"${playlist?.name}" hero'dan kaldırıldı.`,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Hero silme hatası:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || "Hero'dan kaldırılırken bir hata oluştu.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!playlist) return null;

  const isEditMode = !!existingHeroPlaylistId;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            {isEditMode ? 'Hero Ayarlarını Düzenle' : "Hero'ya Gönder"}
          </DialogTitle>
          <DialogDescription>
            <strong>"{playlist.name}"</strong> {isEditMode ? 'hero ayarlarını düzenle.' : 'playlist\'ini manager ana sayfasında öne çıkan olarak göster.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Yükleniyor...</span>
          </div>
        ) : (
          <div className="py-4 space-y-4">
            {/* Playlist Bilgisi */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              {playlist.artwork ? (
                <img
                  src={playlist.artwork.startsWith('https://') ? playlist.artwork : `https://${playlist.artwork}`}
                  alt={playlist.name}
                  className="h-12 w-12 rounded-md object-cover"
                />
              ) : (
                <div className="h-12 w-12 rounded-md bg-slate-200 flex items-center justify-center">
                  <Star className="h-5 w-5 text-slate-400" />
                </div>
              )}
              <div>
                <p className="font-medium">{playlist.name}</p>
                <p className="text-sm text-slate-500">{playlist.songs?.length || 0} şarkı</p>
              </div>
            </div>

            {/* Zamanlama Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-slate-600" />
                <div>
                  <Label htmlFor="schedule-toggle" className="text-sm font-medium">
                    Zamanlama
                  </Label>
                  <p className="text-xs text-slate-500">
                    Belirli tarih aralığında göster
                  </p>
                </div>
              </div>
              <Switch
                id="schedule-toggle"
                checked={useSchedule}
                onCheckedChange={setUseSchedule}
              />
            </div>

            {/* Zamanlama Detayları */}
            {useSchedule && (
              <div className="grid grid-cols-2 gap-3 px-1">
                <div className="space-y-1.5">
                  <Label htmlFor="startDate" className="text-xs">Başlangıç</Label>
                  <Input
                    id="startDate"
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="endDate" className="text-xs">Bitiş</Label>
                  <Input
                    id="endDate"
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Aktif Toggle - Sadece edit modda */}
            {isEditMode && (
              <div className={`flex items-center justify-between p-4 rounded-lg ${isActive ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-3">
                  <Power className={`h-5 w-5 ${isActive ? 'text-green-600' : 'text-red-600'}`} />
                  <div>
                    <Label htmlFor="active-toggle" className="text-sm font-medium">
                      {isActive ? 'Aktif' : 'Pasif'}
                    </Label>
                    <p className="text-xs text-slate-500">
                      {isActive ? 'Manager\'da gösteriliyor' : 'Manager\'da gösterilmiyor'}
                    </p>
                  </div>
                </div>
                <Switch
                  id="active-toggle"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
            )}

            {/* Firma Hedefleme Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-slate-600" />
                <div>
                  <Label htmlFor="targeting-toggle" className="text-sm font-medium">
                    Firma Hedefleme
                  </Label>
                  <p className="text-xs text-slate-500">
                    Sadece seçili firmalara göster
                  </p>
                </div>
              </div>
              <Switch
                id="targeting-toggle"
                checked={useTargeting}
                onCheckedChange={setUseTargeting}
              />
            </div>

            {/* Firma Seçimi */}
            {useTargeting && (
              <div className="space-y-3 px-1">
                <div className="flex gap-2">
                  <Input
                    placeholder="Firma adı yazın..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={selectAllCompanies}
                    type="button"
                    size="sm"
                  >
                    Tümü
                  </Button>
                  {targetCompanyIds.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={clearAllCompanies}
                      type="button"
                      size="sm"
                    >
                      Temizle
                    </Button>
                  )}
                </div>

                <ScrollArea className="h-[200px] border rounded-lg">
                  <div className="p-2 space-y-1">
                    {filteredCompanies.map((company) => (
                      <div
                        key={company._id}
                        onClick={() => handleCompanyToggle(company._id)}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-slate-100 ${
                          targetCompanyIds.includes(company._id) ? 'bg-slate-100' : ''
                        }`}
                      >
                        <span className="text-sm">{company.name}</span>
                        {targetCompanyIds.includes(company._id) && (
                          <Check className="h-4 w-4 text-slate-600" />
                        )}
                      </div>
                    ))}
                    {filteredCompanies.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Firma bulunamadı
                      </p>
                    )}
                  </div>
                </ScrollArea>

                <p className="text-xs text-slate-500">
                  {targetCompanyIds.length > 0
                    ? `${targetCompanyIds.length} firma seçildi`
                    : 'Firma seçilmedi - tüm firmalara gösterilecek'}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          {isEditMode ? (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isSubmitting || isDeleting || isLoading}
              className="mr-auto"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {isDeleting ? 'Kaldırılıyor...' : "Hero'dan Kaldır"}
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting || isDeleting || isLoading}>
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || isDeleting || isLoading}
            >
              {isSubmitting
                ? (isEditMode ? 'Güncelleniyor...' : 'Gönderiliyor...')
                : (isEditMode ? 'Güncelle' : "Hero'ya Gönder")
              }
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
